import { useQuery } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import { AxiosResponse } from 'axios';
import { handleErrorResponse } from '../../utils/handleError';

// API Endpoint and Default Constants
export const APPOINTMENTS_ENDPOINT = '/appointments';
export const DEFAULT_PAGE_NUMBER = 1;
export const PER_PAGE_RECORDS = 10;

// Interfaces for API Parameters and Response
interface FetchAppointmentsParams {
  page_no?: number;
  per_page?: number;
  from_date?: string;
  patient_id: number;
  status?: string[];
  order_by?: string;
  order_direction?: string;
}

interface Appointment {
  id: number;
  patient_name: string;
  confirm_at: string;
  created_at: string;
  appointment_type_name: string;
  preferred_time: string;
  start_time: string;
  status: string;
  end_time: string;
  date: string;
  doctor_name: string;
}

interface FetchAppointmentsResponse {
  appointments: Appointment[];
  total_records: number;
  total_pages: number;
}

const fetchAppointments = async ({
  page_no,
  per_page,
  from_date,
  status,
  patient_id,
  order_by,
  order_direction,
}: FetchAppointmentsParams): Promise<FetchAppointmentsResponse> => {
  const { data }: AxiosResponse = await apiClient.get(APPOINTMENTS_ENDPOINT, {
    params: {
      page_no,
      per_page,
      from_date,
      patient_id,
      status,
      order_by,
      order_direction,
    },
  });

  return data?.data;
};

function useFetchAppointments({
  page_no = DEFAULT_PAGE_NUMBER,
  per_page = PER_PAGE_RECORDS,
  from_date,
  status,
  patient_id,
  order_by,
  order_direction,
}: FetchAppointmentsParams) {
  const { data, isError, isLoading, isRefetching, error } = useQuery({
    queryKey: [
      'appointments',
      patient_id,
      page_no,
      per_page,
      from_date,
      status,
      order_by,
      order_direction,
    ],
    queryFn: () =>
      fetchAppointments({
        page_no,
        per_page,
        from_date,
        status,
        patient_id,
        order_by,
        order_direction
      }),
  });

  if (isError) {
    handleErrorResponse(error, 'Error Fetching Appointments');
  }

  const {
    appointments = [],
    total_pages: totalPages,
    total_records: totalRecords,
  } = data || {};

  return {
    isLoading,
    isError,
    isRefetching,
    appointments,
    totalPages,
    totalRecords,
  };
}

export default useFetchAppointments;
